import React, { useState, useEffect, useCallback } from "react";
import { NavLink, useLocation } from "react-router-dom";
import authService from "../services/authService";
import Logo from "../assets/logo.png"

export default function Header() {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Toggle menu function
  const toggleMenu = () => {
    setIsMenuOpen(prev => !prev);
  };

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/about", label: "About Us" },
    //{ path: '/portfolio', label: 'Portfolio' },
    { path: "/refund-status", label: "Fund Info" },
    { path: "/payment", label: "Payment" },
    { path: "/contact", label: "Contact" },
  ];

  const servicesMenu = [
    //{ path: "/services/staffing", label: "Staffing" },
    //{ path: "/services/business-consulting", label: "Business Consulting" },
    { path: "/taxfiling/indian-tax-filing", label: "Indian Tax Filing" },
  ];

  const taxFilingMenu = [
    { path: "/taxfiling/advance-tax-planning", label: "Advance Tax Planning" },
    { path: "/taxfiling/individual-tax-filing", label: "Individual Tax Filing" },
    { path: "/taxfiling/business-tax-filing", label: "Business Tax Filing" },
    { path: "/taxfiling/tax-preparation", label: "Tax Preparation" },
    { path: "/taxfiling/fbar-facta-filing", label: "FBAR & FATCA Filing" },
    { path: "/taxfiling/itin-assistance", label: "ITIN Assistance" },
    {
      path: "/taxfiling/fdrl-state-notice-resolution",
      label: "Federal & State Notice Resolution",
    },
    { path: "/taxfiling/tax-amendments", label: "Tax Amendments" },
    { path: "/taxfiling/expert-consulting", label: "Expert Consulting" },
  ];

  // Helper to check if the current route is inside a dropdown
  function isDropdownActive(menuArray) {
    return menuArray.some((item) => location.pathname.startsWith(item.path));
  }

  // Function to close the mobile menu
  const closeMenu = useCallback(() => {
    if (window.innerWidth < 992) {
      setIsMenuOpen(false);
    }
  }, []);

  // Close menu when route changes
  useEffect(() => {
    closeMenu();
  }, [location.pathname, closeMenu]);

  // Check authentication status
  useEffect(() => {
    const checkAuth = () => {
      const userAuth = authService.isUserAuthenticated();
      const adminAuth = authService.isAdminAuthenticated();
      setIsAuthenticated(userAuth || adminAuth);
    };

    // Check immediately on mount and route changes
    checkAuth();

    // Optional: Check periodically (every 5 minutes) for token expiration
    // This is much less frequent than before and only needed for long sessions
    const interval = setInterval(checkAuth, 5 * 60 * 1000); // 5 minutes
    return () => clearInterval(interval);
  }, [location.pathname]);

  // Close menu when clicking outside (optional enhancement)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMenuOpen && window.innerWidth < 992) {
        const nav = document.querySelector('.navbar');
        const toggle = document.querySelector('.navbar-toggler');
        if (nav && !nav.contains(event.target) && event.target !== toggle) {
          closeMenu();
        }
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen, closeMenu]);

  return (
    <header
      className="main-header sticky-top bg-white shadow-sm"
      style={{ zIndex: 1030 }}
    >
      <nav className="navbar navbar-expand-lg navbar-light">
        <div className="container-fluid px-3 px-md-4 px-lg-5" style={{ maxWidth: "1400px", margin: "0 auto" }}>
          {/* Logo */}
          <NavLink
            to="/"
            className="navbar-brand d-flex align-items-center"
            style={{ marginRight: "0" }}
          >
            <img
              src={Logo}
              alt="Nexen Consulting Logo"
              className="logo-img"
            />
          </NavLink>

          {/* Mobile Toggle */}
          <button
            className={`navbar-toggler ${!isMenuOpen ? 'collapsed' : ''}`}
            type="button"
            onClick={toggleMenu}
            aria-controls="navbarContent"
            aria-expanded={isMenuOpen}
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon"></span>
          </button>

          {/* Nav Links & Dropdowns and Sign In/Sign Up */}
          <div
            className={`navbar-collapse ${isMenuOpen ? 'show' : 'collapse'}`}
            id="navbarContent"
          >
            {/* Navigation Links */}
            <ul className="navbar-nav ms-lg-4 ms-xl-5 mb-2 mb-lg-0 align-items-lg-center" style={{ flex: 1 }}>
              {navLinks.map(({ path, label }) => (
                <li className="nav-item" key={path}>
                  <NavLink
                    to={path}
                    onClick={closeMenu}
                    className={({ isActive }) =>
                      `nav-link header-link${isActive ? " active highlight-main" : ""}`
                    }
                  >
                    {label}
                  </NavLink>
                </li>
              ))}

              {/* Services Dropdown */}
              <li className="nav-item dropdown">
                <a
                  className={`nav-link dropdown-toggle header-link${isDropdownActive(servicesMenu) ? " active highlight-main" : ""}`}
                  href="#"
                  id="servicesDropdown"
                  role="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  Services
                </a>
                <ul
                  className="dropdown-menu custom-dropdown shadow"
                  aria-labelledby="servicesDropdown"
                >
                  {servicesMenu.map(({ path, label }) => (
                    <li key={path}>
                      <NavLink
                        to={path}
                        onClick={closeMenu}
                        className={({ isActive }) =>
                          `dropdown-item${isActive ? " active highlight-drop" : ""}`
                        }
                      >
                        {label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </li>

              {/* Tax Filing Dropdown */}
              <li className="nav-item dropdown">
                <a
                  className={`nav-link dropdown-toggle header-link${isDropdownActive(taxFilingMenu) ? " active highlight-main" : ""}`}
                  href="#"
                  id="taxFilingDropdown"
                  role="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  Tax Filing
                </a>
                <ul
                  className="dropdown-menu custom-dropdown shadow"
                  aria-labelledby="taxFilingDropdown"
                >
                  {taxFilingMenu.map(({ path, label }) => (
                    <li key={path}>
                      <NavLink
                        to={path}
                        onClick={closeMenu}
                        className={({ isActive }) =>
                          `dropdown-item${isActive ? " active highlight-drop" : ""}`
                        }
                      >
                        {label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </li>
            </ul>

            {/* Sign In / Sign Up buttons - Only show when not authenticated */}
            {!isAuthenticated && (
              <div className="d-flex flex-column flex-lg-row gap-2 align-items-stretch align-items-lg-center">
                <NavLink
                  to="/signin"
                  onClick={closeMenu}
                  className="btn btn-outline-primary rounded-pill fw-bold header-btn"
                >
                  Sign In
                </NavLink>
                <NavLink
                  to="/signup"
                  onClick={closeMenu}
                  className="btn btn-primary rounded-pill fw-bold text-white header-btn"
                  style={{ background: "linear-gradient(90deg,#6B46C1,#D53F8C)" }}
                >
                  Sign Up
                </NavLink>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Custom Styles */}
      <style>{`
        .main-header {
          font-family: 'Poppins', sans-serif;
          min-height: 76px;
          box-shadow: 0 3px 16px #b9bfff23, 0 1.5px 0 #f0f2fb;
        }
        
        .main-header .navbar {
          position: relative;
        }
        
        .main-header .container-fluid {
          box-sizing: border-box;
          position: relative;
          overflow: visible;
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
        }
        
        .navbar-brand {
          flex-shrink: 0;
        }
        
        /* Mobile menu toggle behavior */
        @media (max-width: 991.98px) {
          .navbar-collapse {
            transition: all 0.3s ease;
            width: 100%;
            flex-basis: 100%;
            flex-grow: 1;
            align-items: flex-start;
          }
          
          .navbar-collapse.collapse:not(.show) {
            display: none !important;
          }
          
          .navbar-collapse.collapse.show {
            display: block !important;
            width: 100%;
          }
        }
        
        /* Desktop layout (≥992px) */
        @media (min-width: 992px) {
          .main-header .container-fluid {
            flex-wrap: nowrap;
            align-items: center;
          }
          
          .navbar-collapse {
            display: flex !important;
            flex-basis: auto;
            flex-grow: 1;
            overflow: visible;
            width: auto;
            order: unset !important;
            margin-top: 0;
            align-items: center;
            justify-content: space-between;
          }
          
          .navbar-toggler {
            order: unset !important;
            margin-left: 0;
            display: none;
          }
          
          .navbar-brand {
            order: unset !important;
            margin-right: 0;
            flex-shrink: 0;
          }
        }
        
        .logo-img {
          height: auto;
          width: 180px;
          max-width: 100%;
          object-fit: contain;
          image-rendering: crisp-edges;
        }
        
        /* Desktop: Large screens (≥992px) - Nav spacing */
        @media (min-width: 992px) {
          .navbar-nav {
            margin-left: 2rem !important;
          }
        }
        
        /* Desktop: Extra large screens (≥1200px) - Nav spacing */
        @media (min-width: 1200px) {
          .navbar-nav {
            margin-left: 3rem !important;
          }
        }
        
        .header-link {
          transition: background 0.16s, color 0.19s;
          white-space: nowrap;
          padding: 0.5rem 1rem !important;
          border-radius: 0.5rem;
          margin: 0 0.25rem;
          display: inline-block;
        }
        
        .header-link:hover,
        .header-link:focus {
          background: #f7f2fc;
          color: #6B46C1 !important;
        }
        
        .nav-link.active.highlight-main,
        .nav-link.active.highlight-main:focus {
          color: #D53F8C !important;
          background: linear-gradient(90deg, #fde5fc 40%, #f1efff 100%);
          font-weight: bold;
          border-bottom: 2.5px solid #D53F8C;
        }
        
        .navbar-nav .dropdown {
          position: static;
        }
        
        @media (min-width: 992px) {
          .navbar-nav .dropdown {
            position: relative;
          }
          
          .navbar-nav .dropdown-menu {
            position: absolute;
            top: 100%;
            z-index: 1050;
          }
        }
        
        .dropdown-menu.custom-dropdown {
          border-radius: 1rem;
          min-width: 220px;
          max-width: 320px;
          border: none;
          margin-top: 6px;
          background: #fff;
          box-shadow: 0 4px 28px #d53f8c33;
          animation: fadeIn 0.19s;
          position: absolute;
          top: 100%;
          left: 0;
          z-index: 1050;
          overflow-y: auto;
          max-height: 80vh;
        }
        
        @media (min-width: 992px) {
          .dropdown-menu.custom-dropdown {
            min-width: 240px;
            max-width: 350px;
            z-index: 1050;
          }
          
          /* Prevent dropdown from going off screen */
          .navbar-nav > li.dropdown:last-of-type .dropdown-menu {
            right: 0;
            left: auto;
          }
        }
        
        .dropdown-item {
          transition: background 0.15s, color 0.15s;
          white-space: normal;
          word-wrap: break-word;
          padding: 0.5rem 1rem;
          border-radius: 0.5rem;
          margin: 0.25rem 0;
          line-height: 1.5;
        }
        
        .dropdown-item:hover,
        .dropdown-item:focus {
          background: linear-gradient(90deg, #edeaff 40%, #fff5fb 100%);
          color: #6B46C1 !important;
        }
        
        .dropdown-item.active.highlight-drop {
          background: linear-gradient(90deg, #f9e9fe 60%, #ffe3f2 100%);
          color: #D53F8C !important;
          font-weight: 600;
        }
        
        .header-btn {
          white-space: nowrap;
          padding: 0.5rem 1.25rem !important;
          font-size: 0.9rem;
          transition: all 0.2s ease;
          min-width: 90px;
          box-sizing: border-box;
          flex-shrink: 0;
        }
        
        .header-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
        }
        
        /* Desktop: Large screens (≥992px) */
        @media (min-width: 992px) {
          .navbar-nav {
            position: relative;
            overflow: visible;
            flex-wrap: nowrap;
            gap: 0;
            display: flex;
            align-items: center;
            flex: 1;
            justify-content: flex-start;
          }
          
          .navbar-nav > li {
            flex-shrink: 0;
          }
          
          .navbar-nav > li.dropdown {
            position: relative;
          }
          
          .navbar-nav > li.dropdown:hover > .dropdown-menu {
            display: block;
            opacity: 1;
            visibility: visible;
          }
          
          .dropdown-menu {
            display: none;
            opacity: 0;
            visibility: hidden;
            animation: fadeIn 0.15s;
          }
          
          /* Ensure Tax Filing dropdown aligns properly */
          .navbar-nav > li.dropdown:last-of-type .dropdown-menu {
            right: 0;
            left: auto;
          }
          
          /* Ensure Services dropdown aligns properly */
          .navbar-nav > li.dropdown:nth-last-of-type(2) .dropdown-menu {
            left: 0;
            right: auto;
          }
          
          .header-btn {
            padding: 0.5rem 1.5rem !important;
            font-size: 0.95rem;
            min-width: 100px;
            max-width: 140px;
          }
          
          .d-flex.flex-column.flex-lg-row.gap-2 {
            flex-wrap: nowrap;
            max-width: 300px;
            margin-left: auto;
          }
          
          .navbar-collapse {
            display: flex !important;
            align-items: center;
            justify-content: space-between;
            width: 100%;
            flex-basis: auto;
            flex-grow: 1;
            overflow: visible;
          }
        }
        
        /* Desktop: Extra large screens (≥1200px) */
        @media (min-width: 1200px) {
          .navbar-nav {
            gap: 0;
          }
          
          .header-link {
            padding: 0.5rem 1.25rem !important;
          }
        }
        
        /* Desktop: Large screens (≥1400px) */
        @media (min-width: 1400px) {
          .header-link {
            padding: 0.5rem 1.5rem !important;
          }
        }
        
        /* Tablet: Medium screens (768px - 991px) */
        @media (min-width: 768px) and (max-width: 991.98px) {
          .logo-img {
            width: 160px;
          }
          
          .navbar-nav {
            margin-left: 1.5rem !important;
            flex-wrap: wrap;
          }
          
          .header-link {
            padding: 0.5rem 0.75rem !important;
            font-size: 0.95rem;
          }
          
          .dropdown-menu.custom-dropdown {
            min-width: 200px;
            max-width: 280px;
            position: static !important;
            margin-top: 0.5rem;
            margin-left: 0;
            box-shadow: none;
            border: 1px solid #e9ecef;
          }
          
          .d-flex.flex-column.flex-lg-row.gap-2 {
            max-width: 280px;
            flex-wrap: nowrap;
            margin-left: auto;
          }
          
          .header-btn {
            padding: 0.5rem 1rem !important;
            font-size: 0.85rem;
            min-width: 85px;
            max-width: 130px;
          }
        }
        
        /* Mobile: Small screens (<768px) */
        @media (max-width: 767.98px) {
          .logo-img {
            width: 150px;
          }
          
          .main-header .container-fluid {
            flex-wrap: wrap;
          }
          
          .navbar-collapse {
            width: 100%;
            order: 3;
            margin-top: 0;
            flex-basis: 100%;
          }
          
          .navbar-toggler {
            order: 2;
            margin-left: auto;
          }
          
          .navbar-brand {
            order: 1;
          }
          
          .navbar-nav {
            margin-top: 1rem;
            margin-bottom: 1rem;
            margin-left: 0 !important;
            width: 100%;
          }
          
          .navbar-nav .nav-link {
            padding: 0.75rem 1rem !important;
            text-align: left;
            width: 100%;
          }
          
          .navbar-nav .dropdown-menu {
            position: static !important;
            float: none;
            width: 100%;
            max-width: 100%;
            margin-top: 0.5rem;
            margin-left: 0;
            box-shadow: none;
            border: 1px solid #e9ecef;
            border-radius: 0.5rem;
            max-height: none;
          }
          
          .navbar-nav .dropdown-item {
            white-space: normal;
            word-wrap: break-word;
            padding: 0.75rem 1rem;
          }
          
          .d-flex.flex-column.flex-lg-row.gap-2 {
            width: 100%;
            padding-top: 1rem;
            border-top: 1px solid #e9ecef;
            margin-top: 0.5rem;
          }
          
          .header-btn {
            padding: 0.6rem 1rem !important;
            font-size: 0.85rem;
            width: 100%;
            max-width: 100%;
            min-width: 0;
          }
        }
        
        /* Small Mobile: Extra small screens (<576px) */
        @media (max-width: 575.98px) {
          .logo-img {
            width: 130px;
          }
          
          .header-link {
            padding: 0.7rem 0.9rem !important;
            font-size: 0.9rem;
          }
          
          .dropdown-menu.custom-dropdown {
            min-width: 100%;
          }
          
          .header-btn {
            padding: 0.6rem 0.9rem !important;
            font-size: 0.8rem;
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(14px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </header>
  );
}
